import type { Employment, EmploymentIncome } from "@porchpass/ui";
import {
  DomainAddressType,
  DomainBorrowerEmploymentStatus,
  DomainFrequencyType,
  DomainIncomeType,
} from "@porchpass/api-client";
import type { EmploymentFormData } from "src/types/employment";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import {
  addressErrorsAfterSubmit,
  fromUiAddressPatch,
  toUiAddress,
} from "./address";

function toDomainFrequency(
  frequency?: DomainFrequencyType,
): DomainFrequencyType | undefined {
  return frequency;
}

function fromDomainFrequency(
  frequency: DomainFrequencyType,
): DomainFrequencyType {
  return frequency;
}

function flatToIncomes(dto: EmploymentFormData): EmploymentIncome[] {
  const incomes: EmploymentIncome[] = [];

  if (dto.frequency) {
    incomes.push({
      incomeType: DomainIncomeType.IncomeBase,
      frequency:
        toDomainFrequency(dto.frequency) ??
        DomainFrequencyType.FrequencyMonthly,
      amount: dto.amount ? parseFloat(dto.amount) : undefined,
      weeklyHours: dto.weekly_hours ? parseFloat(dto.weekly_hours) : undefined,
    });
  }

  if (dto.bonus_frequency || (dto.bonus_amount && dto.bonus_amount !== "0")) {
    incomes.push({
      incomeType: DomainIncomeType.IncomeBonus,
      frequency:
        toDomainFrequency(dto.bonus_frequency) ??
        DomainFrequencyType.FrequencyAnnual,
      amount: dto.bonus_amount ? parseFloat(dto.bonus_amount) : undefined,
      weeklyHours: dto.bonus_weekly_hours
        ? parseFloat(dto.bonus_weekly_hours)
        : undefined,
    });
  }

  if (
    dto.commission_frequency ||
    (dto.commission_amount && dto.commission_amount !== "0")
  ) {
    incomes.push({
      incomeType: DomainIncomeType.IncomeCommission,
      frequency:
        toDomainFrequency(dto.commission_frequency) ??
        DomainFrequencyType.FrequencyAnnual,
      amount: dto.commission_amount
        ? parseFloat(dto.commission_amount)
        : undefined,
      weeklyHours: dto.commission_weekly_hours
        ? parseFloat(dto.commission_weekly_hours)
        : undefined,
    });
  }

  if (
    dto.overtime_frequency ||
    dto.overtime_amount ||
    dto.overtime_weekly_hours
  ) {
    incomes.push({
      incomeType: DomainIncomeType.IncomeOvertime,
      frequency:
        toDomainFrequency(dto.overtime_frequency) ??
        DomainFrequencyType.FrequencyHourly,
      amount: dto.overtime_amount ? parseFloat(dto.overtime_amount) : undefined,
      overtimeHours: dto.overtime_weekly_hours
        ? parseFloat(dto.overtime_weekly_hours)
        : undefined,
    });
  }

  return incomes;
}

function incomesToFlat(
  incomes: EmploymentIncome[],
): Partial<EmploymentFormData> {
  const result: Partial<EmploymentFormData> = {
    frequency: undefined,
    amount: "",
    weekly_hours: "",
    bonus_frequency: undefined,
    bonus_amount: "",
    bonus_weekly_hours: "",
    commission_frequency: undefined,
    commission_amount: "",
    commission_weekly_hours: "",
    overtime_frequency: undefined,
    overtime_amount: "",
    overtime_weekly_hours: "",
  };

  const base = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeBase,
  );
  if (base) {
    result.frequency = fromDomainFrequency(base.frequency);
    result.amount = base.amount != null ? String(base.amount) : "";
    result.weekly_hours =
      base.weeklyHours != null ? String(base.weeklyHours) : "";
  }

  const bonus = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeBonus,
  );
  if (bonus) {
    result.bonus_frequency = fromDomainFrequency(bonus.frequency);
    result.bonus_amount = bonus.amount != null ? String(bonus.amount) : "";
    result.bonus_weekly_hours =
      bonus.weeklyHours != null ? String(bonus.weeklyHours) : "";
  }

  const commission = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeCommission,
  );
  if (commission) {
    result.commission_frequency = fromDomainFrequency(commission.frequency);
    result.commission_amount =
      commission.amount != null ? String(commission.amount) : "";
    result.commission_weekly_hours =
      commission.weeklyHours != null ? String(commission.weeklyHours) : "";
  }

  const overtime = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeOvertime,
  );
  if (overtime) {
    result.overtime_frequency = fromDomainFrequency(overtime.frequency);
    result.overtime_amount =
      overtime.amount != null ? String(overtime.amount) : "";
    result.overtime_weekly_hours =
      overtime.overtimeHours != null ? String(overtime.overtimeHours) : "";
  }

  return result;
}

export function toUiEmployment(dto: EmploymentFormData): Employment {
  return {
    id: dto.id,
    employerName: dto.employer_name ?? "",
    jobTitle: dto.position_title ?? "",
    isSelfEmployed: dto.is_self_employed,
    isCurrent: dto.is_current ?? false,
    startMonth: dto.from_month,
    startYear: dto.from_year,
    endMonth: dto.to_month,
    endYear: dto.to_year,
    employmentType: DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed,
    employerAddress: toUiAddress(
      {
        street1: dto.street1,
        street2: undefined,
        city: dto.city,
        county: dto.county,
        state: dto.state,
        zip_code: dto.zip_code,
      },
      DomainAddressType.AddressEmployer,
    ),
    incomes: flatToIncomes(dto),
  };
}

export function fromUiEmploymentPatch(
  patch: Partial<Employment>,
): Partial<EmploymentFormData> {
  const result: Partial<EmploymentFormData> = {};

  if (patch.id !== undefined) result.id = patch.id;
  if (patch.employerName !== undefined) {
    result.employer_name = patch.employerName;
  }
  if (patch.jobTitle !== undefined) result.position_title = patch.jobTitle;
  if (patch.isSelfEmployed !== undefined) {
    result.is_self_employed = patch.isSelfEmployed;
  }
  if (patch.isCurrent !== undefined) {
    result.is_current = patch.isCurrent;
    if (patch.isCurrent) {
      result.to_month = "";
      result.to_year = "";
    }
  }
  if (patch.startMonth !== undefined) result.from_month = patch.startMonth;
  if (patch.startYear !== undefined) result.from_year = patch.startYear;
  if (patch.endMonth !== undefined) result.to_month = patch.endMonth;
  if (patch.endYear !== undefined) result.to_year = patch.endYear;

  if (patch.employerAddress) {
    Object.assign(result, fromUiAddressPatch(patch.employerAddress));
  }

  if (patch.incomes) {
    Object.assign(result, incomesToFlat(patch.incomes));
  }

  return result;
}

/**
 * Applies a UI patch to employment form data. Keys set to `undefined` are
 * applied too: that's how a removed bonus/commission/overtime line clears its
 * frequency, which would otherwise rebuild the line on the next render.
 */
export function applyEmploymentPatch(
  formData: EmploymentFormData,
  patch: Partial<Employment>,
): EmploymentFormData {
  return { ...formData, ...fromUiEmploymentPatch(patch) };
}

export function employmentErrorsAfterSubmit(
  formErrors: {
    employer_name?: { message?: string };
    position_title?: { message?: string };
    from_month?: { message?: string };
    from_year?: { message?: string };
    to_month?: { message?: string };
    to_year?: { message?: string };
    frequency?: { message?: string };
    amount?: { message?: string };
    weekly_hours?: { message?: string };
    bonus_frequency?: { message?: string };
    bonus_amount?: { message?: string };
    commission_frequency?: { message?: string };
    commission_amount?: { message?: string };
    overtime_amount?: { message?: string };
    overtime_weekly_hours?: { message?: string };
    street1?: { message?: string };
    street2?: { message?: string };
    city?: { message?: string };
    county?: { message?: string };
    state?: { message?: string };
    zip_code?: { message?: string };
  },
  hasAttemptedFormSubmission: boolean,
) {
  return {
    employmentErrors: fieldErrorsAfterSubmit(
      {
        employerName: formErrors.employer_name?.message,
        jobTitle: formErrors.position_title?.message,
        startMonth: formErrors.from_month?.message,
        startYear: formErrors.from_year?.message,
        endMonth: formErrors.to_month?.message,
        endYear: formErrors.to_year?.message,
        "incomes.base.frequency": formErrors.frequency?.message,
        "incomes.base.amount": formErrors.amount?.message,
        "incomes.base.weeklyHours": formErrors.weekly_hours?.message,
        "incomes.bonus.frequency": formErrors.bonus_frequency?.message,
        "incomes.bonus.amount": formErrors.bonus_amount?.message,
        "incomes.commission.frequency":
          formErrors.commission_frequency?.message,
        "incomes.commission.amount": formErrors.commission_amount?.message,
        "incomes.overtime.amount": formErrors.overtime_amount?.message,
        "incomes.overtime.overtimeHours":
          formErrors.overtime_weekly_hours?.message,
      },
      hasAttemptedFormSubmission,
    ),
    employerAddressErrors: addressErrorsAfterSubmit(
      formErrors,
      hasAttemptedFormSubmission,
    ),
  };
}
